import React, { FC, MutableRefObject, ReactElement, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    buildDeviceTypeModalSelectOptions,
    getDeviceTypeLabel,
    parseDeviceTypeApiValue
} from '../../../NonReact/constants/DeviceTypeConstants'
import { DeviceType, DeviceTypeApiString } from '../../../NonReact/constants/DeviceTypes'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { PartitionAdminResponse } from '../../../NonReact/Types/Types'
import { getPartitions } from '../../../NonReact/common/API/EntityAPIClient'
import { IsStringEmpty, IsVarEmpty, isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE } from '../../Common/Constants/DefaultValues'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import FormRow from '../../Common/Components/FormRow'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { SelectOptions } from '../../Common/Components/Selects'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { useFormContext, UseFormMethods, useWatch } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { DeviceConfigurationForm } from '../Types/Types'
import { DEFAULT_DEVICE_TYPE } from '../Constants/Constants'
import type { DeviceModalCapabilityFlags } from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH,
    DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS
} from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_DESCRIPTION_FIELD,
    DEVICE_GUID_FIELD,
    DEVICE_ID_ON_TELSYS_DIVISION_FIELD,
    DEVICE_PART_ID_FIELD,
    DEVICE_PARTITION_DISPLAY_FIELD,
    DEVICE_TYPE_FIELD,
    DEVICE_NUMBER_FIELD,
    DEVICE_UPDATE_EXISTING_ITEMS_FIELD
} from '../Constants/FieldNames'

const NONE_PARTITION_VALUE: string = String(ALL_VALUE)

type Props = {
    filterTelSysID: number
    isEditMode: boolean
    viewOnly?: boolean
    /** Device type description from the API (change/view); used for a read-only select label. */
    deviceTypeDisplayLabel?: string
    telSysType?: TelSysTypes
    capabilityFlags: DeviceModalCapabilityFlags
}

/**
 * General tab for the device configuration modal.
 *
 * @param props - Tab props.
 * @returns General fields for device add/change/view.
 */
export const DeviceGeneralTab: FC<Props> = ({
    filterTelSysID,
    isEditMode,
    viewOnly,
    deviceTypeDisplayLabel = EMPTY_STRING,
    telSysType,
    capabilityFlags
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const { control, setValue }: UseFormMethods<DeviceConfigurationForm> = useFormContext<DeviceConfigurationForm>()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const disabled: boolean = viewOnly ?? false
    const [partitionOptions, setPartitionOptions] = useState<SelectOptions<string>>([])
    const [isLoadingPartitions, setIsLoadingPartitions] = useState<boolean>(false)

    const addModeDeviceTypeOptions: SelectOptions<string> = useMemo((): SelectOptions<string> => {
        if (isEditMode) return []

        return buildDeviceTypeModalSelectOptions(language, telSysType)
    }, [isEditMode, language, telSysType])

    const watchedDeviceType: string =
        useWatch({
            control,
            name: DEVICE_TYPE_FIELD,
            defaultValue: DEFAULT_DEVICE_TYPE
        }) ?? DEFAULT_DEVICE_TYPE

    const deviceTypeOptions: SelectOptions<string> = useMemo((): SelectOptions<string> => {
        if (!isEditMode) return addModeDeviceTypeOptions

        const parsedDeviceType: DeviceType = parseDeviceTypeApiValue(watchedDeviceType, DeviceType.Unknown)
        let label: string = watchedDeviceType

        if (parsedDeviceType !== DeviceType.Unknown || watchedDeviceType === DeviceTypeApiString.Unknown) {
            label = getDeviceTypeLabel(parsedDeviceType, language, telSysType)
        } else if (isStringNotEmpty(deviceTypeDisplayLabel)) {
            label = deviceTypeDisplayLabel
        }

        return [{ value: watchedDeviceType, label }]
    }, [addModeDeviceTypeOptions, deviceTypeDisplayLabel, isEditMode, language, telSysType, watchedDeviceType])

    const watchedPartitionID: string =
        useWatch({
            control,
            name: DEVICE_PART_ID_FIELD,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING
    const watchedPartitionDisplay: string =
        useWatch({
            control,
            name: DEVICE_PARTITION_DISPLAY_FIELD,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING

    const watchedDeviceGuid: string =
        useWatch({
            control,
            name: DEVICE_GUID_FIELD,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING

    const guidHelpText: Definable<string> =
        !isEditMode && IsStringEmpty(watchedDeviceGuid)
            ? language.get(DeviceModal.DeviceModal_General_GuidPendingHelp)
            : undefined
    const showOwningDepartment: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH]
    const showUpdateExistingItems: boolean =
        !isEditMode && capabilityFlags[DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS]
    const editModePartitionOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            {
                value: watchedPartitionID,
                label: isStringNotEmpty(watchedPartitionDisplay) ? watchedPartitionDisplay : watchedPartitionID
            }
        ],
        [watchedPartitionDisplay, watchedPartitionID]
    )

    useEffect((): void => {
        if (isEditMode) {
            setPartitionOptions([])
            setIsLoadingPartitions(false)
        } else {
            setIsLoadingPartitions(true)

            getPartitions(filterTelSysID)
                .then((partitions: PartitionAdminResponse[]): void => {
                    if (!isMounted.current) return

                    const nextPartitionOptions: SelectOptions<string> = partitions
                        .filter((partition: PartitionAdminResponse): boolean => !IsVarEmpty(partition.PartIDInTelSys))
                        .map((partition: PartitionAdminResponse) => {
                            const partIDInTelSys: number = Number(partition.PartIDInTelSys)
                            const partitionValue: string = String(partIDInTelSys)
                            const partitionLabel: string = isStringNotEmpty(partition.Description)
                                ? partition.Description
                                : partitionValue

                            return {
                                value: partitionValue,
                                label: partitionLabel
                            }
                        })

                    setPartitionOptions([
                        {
                            value: NONE_PARTITION_VALUE,
                            label: language.get(DeviceModal.DeviceModal_General_None)
                        },
                        ...nextPartitionOptions
                    ])
                    setIsLoadingPartitions(false)
                })
                .catch((): void => {
                    if (!isMounted.current) return

                    setPartitionOptions([
                        {
                            value: NONE_PARTITION_VALUE,
                            label: language.get(DeviceModal.DeviceModal_General_None)
                        }
                    ])
                    setIsLoadingPartitions(false)
                })
        }
    }, [filterTelSysID, isEditMode])

    useEffect((): void => {
        if (isEditMode || partitionOptions.length === 0) return

        if (IsStringEmpty(watchedPartitionID)) {
            setValue(DEVICE_PART_ID_FIELD, partitionOptions[0].value)
        }
    }, [isEditMode, partitionOptions, setValue, watchedPartitionID])

    useEffect((): void => {
        if (isEditMode) return

        const selectedPartitionLabel: string =
            partitionOptions.find((partitionOption) => partitionOption.value === watchedPartitionID)?.label ??
            EMPTY_STRING

        setValue(DEVICE_PARTITION_DISPLAY_FIELD, selectedPartitionLabel)
    }, [isEditMode, partitionOptions, setValue, watchedPartitionID])

    return (
        <div className='device-general-tab tab-area'>
            <FormRow>
                <OutlinedSelect
                    name={DEVICE_TYPE_FIELD}
                    label={language.get(DeviceModal.DeviceModal_General_DeviceType)}
                    options={deviceTypeOptions}
                    disabled={isEditMode || disabled}
                    rules={
                        !isEditMode && !disabled
                            ? {
                                  required: language.get(DeviceModal.DeviceModal_General_DeviceTypeRequired)
                              }
                            : undefined
                    }
                />
            </FormRow>

            <FormRow>
                <ValidationErrors name={DEVICE_NUMBER_FIELD}>
                    <OutlinedTextInput
                        inputName={DEVICE_NUMBER_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_DeviceNumber)}
                        rules={{
                            required: language.get(DeviceModal.DeviceModal_General_DeviceNumberRequired)
                        }}
                        disabled={isEditMode || disabled}
                    />
                </ValidationErrors>
            </FormRow>

            <FormRow>
                <ValidationErrors name={DEVICE_DESCRIPTION_FIELD}>
                    <OutlinedTextInput
                        inputName={DEVICE_DESCRIPTION_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_Description)}
                        rules={{
                            required: language.get(DeviceModal.DeviceModal_General_DescriptionRequired)
                        }}
                        disabled={disabled}
                    />
                </ValidationErrors>
            </FormRow>

            <FormRow>
                <OutlinedTextInput
                    inputName={DEVICE_GUID_FIELD}
                    labelLeft={language.get(DeviceModal.DeviceModal_General_IdentifierGuid)}
                    disabled
                    helpText={guidHelpText}
                    placeholder={
                        !isEditMode ? language.get(DeviceModal.DeviceModal_General_IdentifierPlaceholder) : undefined
                    }
                />
            </FormRow>

            <FormRow>
                <ValidationErrors name={DEVICE_PART_ID_FIELD}>
                    <OutlinedSelect
                        name={DEVICE_PART_ID_FIELD}
                        label={language.get(DeviceModal.DeviceModal_General_Partition)}
                        options={isEditMode ? editModePartitionOptions : partitionOptions}
                        disabled={
                            isEditMode ||
                            disabled ||
                            (!isEditMode && (isLoadingPartitions || partitionOptions.length === 0))
                        }
                        rules={
                            !isEditMode
                                ? {
                                      required: language.get(DeviceModal.DeviceModal_General_PartitionRequired),
                                      validate: (value: string): string | true =>
                                          value !== NONE_PARTITION_VALUE ||
                                          language.get(DeviceModal.DeviceModal_General_PartitionRequired)
                                  }
                                : undefined
                        }
                    />
                </ValidationErrors>
            </FormRow>

            {showOwningDepartment ? (
                <FormRow>
                    <OutlinedTextInput
                        inputName={DEVICE_ID_ON_TELSYS_DIVISION_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_IDOnTelSysDivision)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showUpdateExistingItems ? (
                <FormRow>
                    <RouterCheckbox
                        name={DEVICE_UPDATE_EXISTING_ITEMS_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_UpdateExistingItems)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}
        </div>
    )
}
